import React from 'react';
import { motion } from 'framer-motion';
import { X, Bell, Calendar, Thermometer, Lock } from 'lucide-react';
import { usePeriodStore } from '../../store/periodStore';

interface SettingsDialogProps {
  onClose: () => void;
}

export function SettingsDialog({ onClose }: SettingsDialogProps) {
  const { settings, updateSettings } = usePeriodStore();

  const handleToggle = (setting: keyof typeof settings) => {
    updateSettings({ [setting]: !settings[setting] });
  };

  const settingsOptions = [
    {
      id: 'cycleLength',
      label: 'Cycle Length',
      type: 'number',
      value: settings.cycleLength,
      min: 21,
      max: 35,
      hint: '21-35 days'
    },
    {
      id: 'periodLength',
      label: 'Period Length',
      type: 'number',
      value: settings.periodLength,
      min: 2,
      max: 10,
      hint: '2-10 days'
    },
    {
      id: 'trackTemperature',
      label: 'Temperature',
      type: 'toggle',
      value: settings.trackTemperature,
      hint: 'Track BBT'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      type: 'toggle',
      value: settings.notifications,
      hint: 'Reminders'
    }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border max-w-sm w-full overflow-hidden max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold">Settings</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 space-y-4 overflow-y-auto">
          {settingsOptions.map((option) => (
            <div key={option.id} className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">{option.label}</div>
                <div className="text-xs text-muted-foreground">{option.hint}</div>
              </div>
              {option.type === 'number' ? (
                <input
                  type="number"
                  value={option.value}
                  onChange={(e) => updateSettings({ [option.id]: parseInt(e.target.value) })}
                  className="w-20 px-2 py-1 bg-muted rounded-lg text-center"
                  min={option.min}
                  max={option.max}
                />
              ) : (
                <button
                  onClick={() => handleToggle(option.id as keyof typeof settings)}
                  className={`w-11 h-6 rounded-full transition-colors relative ${
                    option.value ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      option.value ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              )}
            </div>
          ))}

          <div className="pt-2 text-xs text-muted-foreground">
            <Lock className="w-3 h-3 inline mr-1" />
            Your data is stored locally and never shared
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border shrink-0">
          <button onClick={onClose} className="w-full theme-button py-2 rounded-lg text-sm">
            Save Changes
          </button>
        </div>
      </motion.div>
    </div>
  );
}